import React, { memo, useCallback, useState } from "react";
import {
  Button,
  HStack,
  Input,
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useDisclosure,
} from "@chakra-ui/react";
import colors from "app/colors";
import { LinkIcon } from "modules/icons";
import { IQuillFormats } from "modules/quill/use-quill";
import { IS_APPLE } from "modules/utils/Constants";
import { normalizeUrl } from "modules/utils/Functions";

interface IProps {
  format: (name: string, value: any) => void;
  insertLink: (url: string) => void;
  activeFormats: IQuillFormats;
}

const ComposeQuillLink = ({ format, insertLink, activeFormats }: IProps) => {
  const isSelected = !!activeFormats.link;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [url, setUrl] = useState("");

  const onSave = useCallback(() => {
    if (url) {
      insertLink(normalizeUrl(url));
    }
    setUrl("");
    onClose();
  }, [url, insertLink, onClose]);

  const _onOpen = useCallback(() => {
    setUrl(typeof activeFormats.link === "string" ? activeFormats.link : "");
    onOpen();
  }, [activeFormats.link, onOpen]);

  const onRemove = useCallback(() => {
    format("link", false);
    setUrl("");
    onClose();
  }, [format, onClose]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.stopPropagation();
        e.preventDefault();
        onSave();
      }
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        onClose();
      }
    },
    [onSave, onClose],
  );

  const bg = isSelected ? colors.gray0 : colors.transparent;

  if (isSelected) {
    return (
      <Button
        variant="hover"
        size="sm"
        aria-label="Format text as link"
        tabIndex={-1}
        onClick={onRemove}
      >
        <LinkIcon boxSize={5} fill={colors.gray40} bg={bg} borderRadius={4} />
      </Button>
    );
  }

  return (
    <Popover
      isOpen={isOpen}
      onOpen={_onOpen}
      onClose={onClose}
      placement="bottom-start"
      isLazy
    >
      <PopoverTrigger>
        <Button
          variant="hover"
          size="sm"
          aria-label="Format text as link"
          title={IS_APPLE ? "Link (⌘K)" : "Link (Ctrl+K)"}
          tabIndex={-1}
          px={0}
        >
          <LinkIcon boxSize={5} fill={colors.gray40} bg={bg} borderRadius={4} />
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent p={2}>
          <PopoverArrow />
          <HStack spacing={2}>
            <Input
              size="sm"
              placeholder="enter URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={onKeyDown}
              autoFocus
              w="250px"
            />
            <Button variant="ghost" size="sm" onClick={onSave}>
              Save
            </Button>
            {isSelected && (
              <Button
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={onRemove}
              >
                Remove
              </Button>
            )}
          </HStack>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default memo(ComposeQuillLink);
